import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

export type UserRole = 'personal' | 'student' | 'enterprise' | 'school' | 'admin' | 'platform' | null

interface AuthContextType {
  isLoggedIn: boolean
  userRole: UserRole
  login: (role: UserRole, token: string, refreshToken?: string) => void
  logout: () => void
}

const AUTH_EXPIRED_EVENT = 'hr:auth-expired'
const AUTH_STORAGE_KEYS = [
  'token', 'role', 'refresh_token', 'audit_status', 'audit_note',
  'user_id', 'currentUserId', 'need_bindphone', 'original_role',
  'user_name', 'user_phone', 'current_user_phone',
]

function clearAuthStorage() {
  AUTH_STORAGE_KEYS.forEach(key => localStorage.removeItem(key))
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('token'))
  const [userRole, setUserRole] = useState<UserRole>(localStorage.getItem('role') as UserRole || null)

  const login = useCallback((role: UserRole, token: string, refreshToken?: string) => {
    localStorage.setItem('token', token)
    if (role) localStorage.setItem('role', role)
    if (refreshToken) localStorage.setItem('refresh_token', refreshToken)
    setIsLoggedIn(true)
    setUserRole(role)
  }, [])

  const logout = useCallback(() => {
    clearAuthStorage()
    setIsLoggedIn(false)
    setUserRole(null)
  }, [])

  useEffect(() => {
    const handleExpired = () => {
      clearAuthStorage()
      setIsLoggedIn(false)
      setUserRole(null)
    }
    window.addEventListener(AUTH_EXPIRED_EVENT, handleExpired)
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handleExpired)
  }, [])

  return (
    <AuthContext.Provider value={{ isLoggedIn, userRole, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
